// utils/navigation.ts

/**
 * 返回路由检查选项
 */
export interface BackRouteCheckOptions {
  /** 是否检查域名 */
  checkDomain?: boolean;
  /** 回退路径（不同域名时使用） */
  fallbackPath?: string;
  /** 询问用户返回外部网站（根据您的要求，已去除） */
  confirmExternal?: boolean;
  /** 自定义历史记录存储key */
  storageKey?: string;
  /** 是否新窗口打开 - 如果为true则强制按新窗口处理 */
  isNewWindow?: boolean;
  /** 安全模式：宽松的返回策略，优先尝试返回上一页（默认开启） */
  safeMode?: boolean;
}

/**
 * 返回路由检查结果
 */
export interface BackRouteResult {
  /** 是否可以返回 */
  canGoBack: boolean;
  /** 是否同一域名 */
  isSameDomain?: boolean;
  /** 是否新窗口打开 */
  isNewWindow?: boolean;
  /** 上一级路由信息 */
  previousRoute?: {
    path: string;
    domain: string;
    fullUrl: string;
  };
  /** 消息 */
  message?: string;
}

/**
 * 检测是否是新窗口打开的页面 - 优化版
 */
const checkIsNewWindow = (): boolean => {
  try {
    // 方法1: 检查 window.opener（最准确）
    if (window.opener) {
      console.log('检测到新窗口打开: window.opener 存在');
      return true;
    }

    // 方法2: 检查 Performance API（现代浏览器支持）
    if (window.performance) {
      const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
      if (navigation) {
        // 如果是前进后退，肯定不是新窗口
        if (navigation.type === 'back_forward') {
          console.log('检测到前进后退导航，不是新窗口');
          return false;
        }
        // 如果是刷新，不是新窗口
        if (navigation.type === 'reload') {
          console.log('检测到页面刷新，不是新窗口');
          return false;
        }
      }
    }

    // 方法3: 有历史记录肯定不是新窗口
    if (window.history.length > 1) {
      console.log(`history.length = ${window.history.length}，不是新窗口`);
      return false;
    }

    // 方法4: history.length === 1 但有 referrer，说明是从其他页面跳转过来的
    if (window.history.length === 1 && document.referrer) {
      console.log('history.length=1 但有 referrer，不是新窗口');
      return false;
    }

    // 方法5: 检查 sessionStorage 中的临时标记
    const newWindowFlag = sessionStorage.getItem('__new_window_flag__');
    if (newWindowFlag === 'true') {
      console.log('检测到新窗口: sessionStorage 标记');
      return true;
    }

    // 方法6: history.length === 1 且无 referrer，才是新窗口
    if (window.history.length === 1 && !document.referrer) {
      console.log('检测到新窗口: history.length=1 且无 referrer');
      return true;
    }

    return false;
  } catch (error) {
    console.warn('检测新窗口时出错:', error);
    return false;
  }
};

/**
 * 为新窗口打开的页面设置标记
 * 可以在打开新窗口的代码中调用
 */
export const markAsNewWindow = (): void => {
  try {
    // 设置 sessionStorage 标记
    sessionStorage.setItem('__new_window_flag__', 'true');
    console.log('已标记为新窗口打开');
  } catch (error) {
    console.warn('设置新窗口标记失败:', error);
  }
};

/**
 * 打开新窗口并标记
 */
export const openNewWindow = (url: string, target = '_blank', features?: string): Window | null => {
  // 先标记
  sessionStorage.setItem('__new_window_flag__', 'true');

  // 打开新窗口
  const newWindow = window.open(url, target, features);

  return newWindow;
};

/**
 * 简化的新窗口检测函数 - 优化版
 */
export const isLikelyNewWindow = (): boolean => {
  try {
    // 1. 如果有 opener，肯定是新窗口
    if (window.opener) {
      return true;
    }

    // 2. 检查 sessionStorage 中的临时标记
    const newWindowFlag = sessionStorage.getItem('__new_window_flag__');
    if (newWindowFlag === 'true') {
      return true;
    }

    // 3. 检查 Performance API
    if (window.performance) {
      const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
      if (navigation) {
        if (navigation.type === 'back_forward' || navigation.type === 'reload') {
          return false;
        }
      }
    }

    // 4. 有历史记录肯定不是新窗口（关键优化）
    if (window.history.length > 1) {
      return false;
    }

    // 5. history.length === 1 但有 referrer，不是新窗口
    if (window.history.length === 1 && document.referrer) {
      return false;
    }

    // 6. 默认：只有当 history.length === 1 且无 referrer 时才认为是新窗口
    return window.history.length === 1 && !document.referrer;
  } catch (error) {
    console.warn('检测新窗口时出错:', error);
    return false;
  }
};

/**
 * 全局封装的 ensureBack 方法 - 优化版
 */
export const ensureBack = (
  callback?: () => void,
  options: BackRouteCheckOptions = {}
): BackRouteResult | void => {
  const {
    checkDomain = true,
    fallbackPath = '/',
    storageKey = 'app_route_history',
    isNewWindow = undefined,
    safeMode = true
  } = options;

  // 检测是否是新窗口（如果未手动指定）
  let detectedIsNewWindow = false;
  if (isNewWindow !== undefined) {
    detectedIsNewWindow = isNewWindow;
  } else {
    detectedIsNewWindow = isLikelyNewWindow();
    
    // 安全模式下，如果有历史记录，强制认为不是新窗口
    if (safeMode && window.history.length > 1) {
      if (detectedIsNewWindow) {
        console.log('安全模式修正：history.length > 1，强制认为不是新窗口');
        detectedIsNewWindow = false;
      }
    }
  }

  // 实际执行返回检查的逻辑
  const executeBackCheck = (): BackRouteResult => {
    const result: BackRouteResult = {
      canGoBack: false,
      isNewWindow: detectedIsNewWindow,
      message: detectedIsNewWindow ? '新窗口打开' : '非新窗口打开'
    };

    try {
      // 如果是新窗口打开，直接设置不能返回，需要跳转首页
      if (detectedIsNewWindow) {
        result.canGoBack = false;
        result.message = '新窗口打开，将返回首页';
        return result;
      }

      // 优先检查浏览器历史记录（优化点）
      if (window.history.length > 1) {
        result.canGoBack = true;
        result.message = '浏览器历史可返回';
        return result;
      }

      // 没有浏览器历史时，再检查自定义路由历史
      const previousInfo = getPreviousRouteInfo(checkDomain, storageKey);

      if (!previousInfo) {
        result.canGoBack = false;
        result.message = '无历史记录，将返回首页';
        return result;
      }

      result.canGoBack = true;
      result.previousRoute = previousInfo;
      result.isSameDomain = previousInfo.domain === window.location.origin;

      if (result.isSameDomain) {
        result.message = '同一域名，可安全返回';
      } else {
        result.message = '不同域名，将返回首页';
      }

      return result;
    } catch (error) {
      console.error('返回检查出错:', error);
      result.message = '检查出错';
      return result;
    }
  };

  // 执行返回操作
  const performBack = (result: BackRouteResult) => {
    // 关键修复：如果 canGoBack 为 false，直接跳转首页
    if (!result.canGoBack) {
      console.log('无法返回，跳转到首页:', fallbackPath);
      window.location.href = fallbackPath;
      if (typeof callback === 'function') callback();
      return;
    }

    // vivo 浏览器特殊处理：只要有历史记录就直接返回，不检查其他条件
    if (isVivoBrowser()) {
      if (window.history.length > 1) {
        console.log('vivo浏览器，history.length > 1，执行 history.go(-1)');
        window.history.go(-1);
        if (typeof callback === 'function') callback();
        return;
      } else {
        console.log('vivo浏览器，history.length <= 1，跳转首页:', fallbackPath);
        window.location.href = fallbackPath;
        if (typeof callback === 'function') callback();
        return;
      }
    }

    // 安全模式下的额外处理：优先尝试浏览器后退
    if (safeMode && window.history.length > 1) {
      console.log('安全模式：优先执行浏览器后退');
      window.history.back();
      if (typeof callback === 'function') callback();
      return;
    }

    // 新窗口打开，跳转到首页
    if (result.isNewWindow) {
      console.log('新窗口打开，跳转到首页:', fallbackPath);
      window.location.href = fallbackPath;
      if (typeof callback === 'function') callback();
      return;
    }

    if (result.isSameDomain || !checkDomain) {
      // 同一域名或不需要检查域名，直接返回
      console.log('执行 history.back()');
      window.history.back();
      if (typeof callback === 'function') callback();
      return;
    }

    // 不同域名，直接返回首页
    console.log('不同域名，跳转到首页:', fallbackPath);
    window.location.href = fallbackPath;
    if (typeof callback === 'function') callback();
  };

  // 如果有回调函数，执行检查并返回
  if (typeof callback === 'function') {
    const result = executeBackCheck();
    performBack(result);
  } else {
    // 没有回调，只返回检查结果
    return executeBackCheck();
  }
};

/**
 * 简化的返回函数
 */
export const safeBack = (
  fallbackPath: string = '/',
  options: Omit<BackRouteCheckOptions, 'fallbackPath'> = {}
) => {
  ensureBack(() => {}, { ...options, fallbackPath });
};

/**
 * 强制返回首页（忽略所有检查）
 */
export const goHome = (homePath: string = '/') => {
  window.location.href = homePath;
};

/**
 * 安全的后退按钮点击处理
 */
export const handleBackButton = (
  event?: React.MouseEvent,
  options: BackRouteCheckOptions = {}
) => {
  if (event) {
    event.preventDefault();
    event.stopPropagation();
  }

  safeBack(options.fallbackPath, options);
};

/**
 * 获取上一级路由信息
 */
const getPreviousRouteInfo = (
  checkDomain: boolean,
  storageKey: string
) => {
  // 1. 首先检查自定义路由历史记录
  const storedHistory = getStoredRouteHistory(storageKey);
  if (storedHistory.length > 1) {
    const previousRoute = storedHistory[storedHistory.length - 2];
    return {
      path: previousRoute.path,
      domain: previousRoute.domain,
      fullUrl: previousRoute.fullUrl
    };
  }

  // 2. 检查 referrer
  if (checkDomain && document.referrer) {
    try {
      const referrerUrl = new URL(document.referrer);
      return {
        path: referrerUrl.pathname + referrerUrl.search,
        domain: referrerUrl.origin,
        fullUrl: document.referrer
      };
    } catch (error) {
      console.warn('解析 referrer 失败:', error);
    }
  }

  return null;
};

/**
 * 获取存储的路由历史
 */
const getStoredRouteHistory = (storageKey: string) => {
  try {
    const stored = sessionStorage.getItem(storageKey);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.warn('读取路由历史失败:', error);
    return [];
  }
};

/**
 * 检测是否是 vivo 浏览器
 */
const isVivoBrowser = (): boolean => {
  const ua = navigator.userAgent.toLowerCase();
  return ua.includes('vivobrowser');
};